import { CompositeDelta, StatusBadge, TierPill } from "./EngagementBadges";
import Button from "../../Components/Button";
import { fmtScore, MCI_MUTED } from "../../Utils/Helper/sponsorEngagementUi";

type Props = {
  account: {
    account_id: string;
    account_name: string;
    theater?: string;
    region?: string;
    sponsor_role?: "primary" | "secondary" | string | null;
    poll1_submitted_at?: string | null;
    composite?: number | null;
    tier?: string | null;
    composite_delta?: number | null;
  };
  isDraft: boolean;
  showStatusBadge?: "draft-only" | "completed-only" | "none";
  onAction: () => void;
};

const sponsorRoleLabel = (role?: string | null): string | null => {
  if (role === "primary") return "Primary Sponsor";
  if (role === "secondary") return "Secondary Sponsor";
  return null;
};

const AccountCheckInCard = ({
  account,
  isDraft,
  showStatusBadge = "draft-only",
  onAction,
}: Props) => {
  const submitted = Boolean(account.poll1_submitted_at);
  const status = submitted ? "completed" : isDraft ? "draft" : "pending";
  const showBadge =
    (showStatusBadge === "draft-only" && isDraft && !submitted) ||
    (showStatusBadge === "completed-only" && submitted);
  const meta = [
    ...(account.theater === account.region
      ? [account.theater]
      : [account.theater, account.region]),
    sponsorRoleLabel(account.sponsor_role),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`relative bg-white border border-gray-200 rounded-lg px-4 py-3.5 flex flex-col gap-2 ${
        submitted
          ? ""
          : "cursor-pointer transition-all duration-150 hover:border-gray-300 hover:shadow-sm"
      }`}
      role={submitted ? undefined : "button"}
      tabIndex={submitted ? undefined : 0}
      onClick={submitted ? undefined : onAction}
      onKeyDown={
        submitted
          ? undefined
          : e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAction();
              }
            }
      }
    >
      {submitted && account.tier ? (
        <div className="absolute top-3 right-3">
          <TierPill tier={account.tier} />
        </div>
      ) : null}
      {showBadge ? (
        <div className="absolute top-3 right-3">
          <StatusBadge status={status} />
        </div>
      ) : null}
      <div>
        <div className="text-sm font-semibold text-gray-900 leading-snug pr-20">
          {account.account_name}
        </div>
        {meta ? (
          <div className={`text-xs ${MCI_MUTED} mt-0.5`}>{meta}</div>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 mt-auto">
        {submitted && account.composite != null ? (
          <span className="text-sm flex items-center gap-1">
            <span className={MCI_MUTED}>Score</span>
            <span className="text-base font-semibold text-gray-900">
              {fmtScore(account.composite, 2)}
            </span>
            <span className={MCI_MUTED}>/ 5.0</span>
          </span>
        ) : (
          <span className={`text-sm ${MCI_MUTED}`}>No score yet</span>
        )}
        <div className="flex items-center gap-2">
          {submitted ? (
            <CompositeDelta delta={account.composite_delta} />
          ) : null}
          <Button
            variant={submitted ? "secondary" : "primary"}
            className="text-sm py-1 px-2.5"
            onClick={e => {
              e.stopPropagation();
              onAction();
            }}
          >
            {submitted ? "View" : isDraft ? "Resume" : "Start Check-In →"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountCheckInCard;
